import axios from "@/utils/axios";
import { propOr, pathOr, tap, path, compose, length, map, objOf } from "ramda";
import { NUMBER_ELEMENTS_PER_PAGE } from "@/constants/generic";
import { DefaultCategoryOptions, DefaultShareableOptions } from "@/constants/studySets";
import {
  SET_CREATED_ASSIGNMENTS,
  UPDATE_BUILDING_ASSIGNMENTS,
  RESET_BUILDING_ASSIGNMENTS,
  RESET_STUDYSETS,
  ADD_STUDYSETS,
  UPDATE_BUILDING_STUDYSET,
  RESET_BUILDING_STUDYSET,
  ADD_STUDYSET,
  SET_CURRENT_STUDYSET,
  SET_CATEGORIES,
  SET_STUDY_SET_FILTERS,
  SET_STUDY_SET_IS_FILTER,
} from "../constants";
import { format } from "date-fns";

const headers = {
  "x-session-token": localStorage.getItem("payment-token"),
};

const initialState = () => ({
  // Object that represents that class that is being created
  buildingStudySet: {
    name: "",
    phrases: [],
    shareable: DefaultShareableOptions[0].value, //organization, group, public
    category: DefaultCategoryOptions[0].value,
  },
  buildingAssignments: {
    studySets: [],
    assessments: [],
    assignments: [],
  },
  // List of all classes for the default organization
  list: [],
  categories: ["IELTS", "TOEIC", "Culture", "Food", "Music and Movies", "Relationships", "Travel", "Work"],
  // Selected class
  currentStudySet: {},
  createdAssignments: [],
  filters: {
    category: [],
    created_by: [],
    shareable: [],
  },
  isFilter: false,
});

const state = initialState();

const getters = {
  studysetsSize: compose(length, pathOr([], ["list", "results"])),
  getBuildingStudySet: propOr({}, "buildingStudySet"),
  getBuildingAssignments: propOr({}, "buildingAssignments"),
  getCreatedAssignments: propOr([], "createdAssignments"),
  getStudySetList: pathOr([], ["list", "results"]),
  getCurrentStudySet: propOr({}, "currentStudySet"),
  getStudySetOverview: pathOr({}, ["currentStudySet", "lessons"]),
  buildPagination(state) {
    const pageCounter = getters.studysetsSize(state);
    const numberPages = state.list.count / NUMBER_ELEMENTS_PER_PAGE;
    return {
      pageCounter,
      total: state.list.count || 0,
      next: state.list.next,
      previous: state.list.previous,
      numberPages: Math.floor(numberPages),
    };
  },
  getCurrentStudySetId: path(["currentStudySetClass", "id"]),
  getStudySetCategories: propOr([], "categories"),
};

const mutations = {
  [ADD_STUDYSETS](state, studySets) {
    state.list = studySets;
  },
  [UPDATE_BUILDING_STUDYSET](state, updatedStudySets) {
    state.buildingStudySet = {
      ...state.buildingStudySet,
      ...updatedStudySets,
    };
  },
  [UPDATE_BUILDING_ASSIGNMENTS](state, updatedBuildingAssignments) {
    state.buildingAssignments = {
      ...state.buildingAssignments,
      ...updatedBuildingAssignments,
    };
  },
  [RESET_BUILDING_STUDYSET](state) {
    state.buildingStudySet = {
      name: "",
      phrases: [],
      shareable: DefaultShareableOptions[0].value, //organization, group, public
      category: DefaultCategoryOptions[0].value,
    };
  },
  [RESET_BUILDING_ASSIGNMENTS](state) {
    state.buildingAssignments = {
      assessments: [],
      studySets: [],
      assignments: [],
    };
  },
  [ADD_STUDYSET](state, studySetObject) {
    state.list = [...state.list, studySetObject];
  },
  [SET_CURRENT_STUDYSET](state, currentStudySet) {
    state.currentStudySet = currentStudySet;
  },
  [RESET_STUDYSETS](state) {
    Object.assign(state, initialState());
  },
  [SET_CATEGORIES](state, categories) {
    state.categories = categories;
  },
  [SET_CREATED_ASSIGNMENTS](state, createdAssignments) {
    state.createdAssignments = createdAssignments;
  },
  [SET_STUDY_SET_FILTERS](state, filters) {
    state.filters = filters;
  },
  [SET_STUDY_SET_IS_FILTER](state, value) {
    state.isFilter = value;
  },
};

const actions = {
  updateBuildingStudySet({ commit }, updatedStudySet) {
    commit(UPDATE_BUILDING_STUDYSET, updatedStudySet);
  },
  updateBuildingAssignments({ commit }, updateAssignments) {
    commit(UPDATE_BUILDING_ASSIGNMENTS, updateAssignments);
  },
  createStudySet({ commit, rootGetters }, studySetObject) {
    const loggedInUser = rootGetters["user/getUser"];
    const organizationId = rootGetters["organization/currentOrganizationId"];
    const requestBody = {
      name: studySetObject.name,
      category: studySetObject.category,
      shareable: studySetObject.shareable,
      phrases: studySetObject.phrases.map(x => x.text),
      owner: loggedInUser.id,
      organization: organizationId,
    };
    return axios.post(`/v1/study-sets/${organizationId}/`, requestBody, { headers }).then(async response => {
      const createdStudySet = response.data;
      commit(SET_CURRENT_STUDYSET, createdStudySet);
      return response;
    });
  },
  resetBuildingStudySet({ commit }) {
    commit(RESET_BUILDING_STUDYSET);
  },
  resetBuildingAssignments({ commit }) {
    commit(RESET_BUILDING_ASSIGNMENTS);
  },
  getStudySet({ dispatch, rootGetters }, { studySetId } = {}) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios
      .get(`/v1/study-sets/${organizationId}/${studySetId}/`)
      .then(tap(response => dispatch("setCurrentStudySet", response.data)));
  },
  setCurrentStudySet({ commit }, currentStudySet) {
    return commit(SET_CURRENT_STUDYSET, currentStudySet);
  },
  updateStudySet({ commit, getters, rootGetters }) {
    const studySetObject = getters["getBuildingStudySet"];
    const loggedInUser = rootGetters["user/getUser"];
    const organizationId = rootGetters["organization/currentOrganizationId"];
    const requestBody = {
      name: studySetObject.name,
      category: studySetObject.category,
      shareable: studySetObject.shareable,
      phrases: studySetObject.phrases.map(x => x.text),
      owner: loggedInUser.id,
      organization: organizationId,
    };

    return axios
      .patch(`/v1/study-sets/${organizationId}/${studySetObject.id}/`, requestBody, { headers })
      .then(response => {
        const newStudySet = {
          ...response.data,
        };
        commit(SET_CURRENT_STUDYSET, newStudySet);
        commit(RESET_BUILDING_STUDYSET);
        return response;
      });
  },
  updateStudySetName({ commit, getters, rootGetters }, { studySetId, payload } = {}) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios.patch(`v1/study-sets/${organizationId}/${studySetId}/`, payload, { headers });
  },
  deleteStudySet({ rootGetters }, studySetId) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios.delete(`/v1/study-sets/${organizationId}/${studySetId}/`, { headers });
  },
  reset({ commit }) {
    return commit(RESET_STUDYSETS);
  },
  getStudySetCategories({ dispatch }) {
    //return axios.get(`/v1/categories/`).then(tap((response) => dispatch('set_categories', response.data)));
    return new Promise();
  },
  createAssignments({ commit, getters, rootGetters }, { assignmentLibraryId, skillModes }) {
    let buildingAssignments = getters["getBuildingAssignments"];
    const assignedClasses = buildingAssignments.assignments;
    const assignedStudySets = buildingAssignments.studySets;
    const assessments = buildingAssignments.assessments;
    const assessmentIds = assessments.map(x => x.id);
    const organizationId = rootGetters["organization/currentOrganizationId"];
    const payload_assignments = [];
    const studySetIds = assignedStudySets.map(x => x.id);
    const now = format(Date.now(), "yyyy-MM-dd HH:mm");
    for (let i = 0; i < assignedClasses.length; i++) {
      const assignment = assignedClasses[i];
      let assignmentWithoutGroup = {
        name: assignment.name,
        // group: assignment.groupId,
        study_sets: studySetIds,
        assessment_ids: assessmentIds,
        due_date: assignment.dueDate + " " + assignment.dueTime,
        start_date: assignment.startTime != "now" ? assignment.startDate + " " + assignment.startTime : now,
        reminder_before_day: assignment.reminderBeforeDay,
        notify_time: assignment.timeNotify,
        assignment_library_id: assignmentLibraryId,
        assign_now: assignment.assign_now,
      };

      if (skillModes != null && "isOneSkill" in skillModes && skillModes.isOneSkill) {
        assignmentWithoutGroup["skill_modes"] = {
          speaking: studySetIds,
        };
      }

      for (let j = 0; j < assignment.groupIds.length; j++) {
        payload_assignments.push({ ...assignmentWithoutGroup, group: assignment.groupIds[j] });
      }
    }
    const requestBody = {
      assignments: payload_assignments,
    };
    return axios.post(`/v1/study-sets/${organizationId}/assignments`, requestBody).then(response => {
      const newAssignments = {
        ...response.data,
      };
      commit(SET_CREATED_ASSIGNMENTS, newAssignments);
      commit(RESET_BUILDING_ASSIGNMENTS);
      return response;
    });
  },
  checkStudySetPermission({ rootGetters }, studySetIds) {
    const requestBody = {
      ids: studySetIds,
    };
    return axios.post("/v1/study-sets/access-check", requestBody).then(response => response.data);
  },
  setFilters({ commit }, filters) {
    return commit(SET_STUDY_SET_FILTERS, filters);
  },
  setIsFilter({ commit }, value) {
    return commit(SET_STUDY_SET_IS_FILTER, value);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
